import { useState } from "react";
import { Box, Typography } from "@mui/material";

import type { AvtFormSection, AvtMappingResult } from "@thalamos/aspire-common";
import {
  mapSectionsToFields,
  parseMarkdownSections,
} from "@thalamos/aspire-common";

import { Button } from "../index.js";
import { TextField } from "../TextField/TextField.js";
import { TortusDictationReview } from "./TortusDictationReview.js";

export type TortusDictationPasteProps = {
  /** The form's section declaration (e.g. `fdmcaAvtSections`). */
  sections: AvtFormSection[];
  /** Field key → human label for the review screen. */
  fieldLabels: Record<string, string>;
  /** Current form values, for the append/replace merge. */
  currentValues: Record<string, string>;
  /** Called with the accepted field → new-value updates. */
  onApply: (updates: Record<string, string>) => void;
  onCancel: () => void;
  /** Optional pre-filled markdown (handy for stories/tests). */
  initialMarkdown?: string;
};

/**
 * Alternative to the live dictation: paste the Tortus markdown note, parse it
 * into sections, map it onto the form, and review/apply — no embed, token, or
 * microphone needed. Reuses {@link TortusDictationReview} for the review step.
 */
export function TortusDictationPaste({
  sections,
  fieldLabels,
  currentValues,
  onApply,
  onCancel,
  initialMarkdown = "",
}: TortusDictationPasteProps) {
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [result, setResult] = useState<AvtMappingResult | null>(null);

  if (result) {
    return (
      <TortusDictationReview
        result={result}
        fieldLabels={fieldLabels}
        currentValues={currentValues}
        onApply={onApply}
        // Back to the paste box so the note can be edited and re-parsed.
        onCancel={() => {
          setResult(null);
        }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6">Paste Tortus note</Typography>
      <Typography variant="body2" color="text.secondary">
        Paste the markdown note from Tortus below, then continue to review how
        it maps onto the form. Nothing is saved until you apply on the next
        screen.
      </Typography>
      <TextField
        name="tortus-note-markdown"
        label=""
        placeholder="## Decision being assessed&#10;..."
        value={markdown}
        multiline
        rows="14"
        useFullWidth
        onChange={(value: string) => {
          setMarkdown(value);
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button
          label="Cancel"
          variant="outlined"
          color="inherit"
          onClick={onCancel}
        />
        <Button
          label="Continue"
          disabled={markdown.trim().length === 0}
          onClick={() => {
            setResult(
              mapSectionsToFields(parseMarkdownSections(markdown), sections),
            );
          }}
        />
      </Box>
    </Box>
  );
}
